/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { loadGoogleMapsScript, parsePlace, type ParsedAddress } from "@/lib/googleMaps";

type InputProps = React.ComponentPropsWithoutRef<typeof Input>;

interface AddressAutocompleteProps extends Omit<InputProps, "onSelect"> {
  onPlaceSelect?: (parsed: ParsedAddress) => void;
  countryRestriction?: string;
}

export const AddressAutocomplete = forwardRef<HTMLInputElement, AddressAutocompleteProps>(
  ({ onPlaceSelect, countryRestriction = "au", ...inputProps }, forwardedRef) => {
    const localRef = useRef<HTMLInputElement | null>(null);
    const autocompleteRef = useRef<any>(null);
    const onPlaceSelectRef = useRef(onPlaceSelect);
    const [mapsReady, setMapsReady] = useState(false);

    useEffect(() => {
      onPlaceSelectRef.current = onPlaceSelect;
    }, [onPlaceSelect]);

    useEffect(() => {
      loadGoogleMapsScript()
        .then(() => setMapsReady(true))
        .catch(() => console.warn("Google Maps failed to load, falling back to plain input."));
    }, []);

    useEffect(() => {
      if (!mapsReady || !localRef.current || autocompleteRef.current) return;

      const ac = new window.google.maps.places.Autocomplete(localRef.current, {
        componentRestrictions: { country: countryRestriction },
        fields: ["address_components", "formatted_address"],
        types: ["address"],
      });

      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const parsed = parsePlace(place);
        if (parsed) onPlaceSelectRef.current?.(parsed);
      });

      autocompleteRef.current = ac;
    }, [mapsReady, countryRestriction]);

    const setRefs = (el: HTMLInputElement | null) => {
      localRef.current = el;
      if (typeof forwardedRef === "function") {
        forwardedRef(el);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    };

    return <Input {...inputProps} ref={setRefs} autoComplete="off" />;
  },
);
AddressAutocomplete.displayName = "AddressAutocomplete";
